/**
 * @fileoverview V2 Police Report Step — Police department + report number.
 *
 * Key difference from V1: PD selection does NOT block the report number field.
 * Both fields are always visible. However, saving requires a department to be
 * selected (or "not found" checked).
 *
 * Audit-tracked: updates `police_report` section as found/not_found.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { Building2, Sparkles, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { PoliceDepartmentPicker } from '@/components/police';
import type { ReadPoliceDepartment } from '@/services/api/generated/policeDepartmentsService';
import {
  useGetCaseIngestionQuery,
  useUpdateCaseIngestionSectionsMutation,
} from '@/services/api/caseIngestionService';
import {
  usePoliceReportsCreateMutation,
  usePoliceReportsDestroyMutation,
  usePoliceReportsListQuery,
  usePoliceReportsPartialUpdateMutation,
} from '@/services/api/policeReportsService';

import {
  FormCheckbox,
  FormError,
  FormField,
  FormGroup,
  FormInput,
  FormLabel,
  FormSection,
  ImportSuggestion,
} from '../components/form';
import { useStepHydration } from '../hooks/useStepHydration';
import { useIngestion } from '../state/IngestionProvider';

// ── Schema ───────────────────────────────────────────────────────────────────

const policeReportSchema = z.object({
  report_identifier: z.string().optional(),
  police_department_id: z.string().optional(),
  not_found: z.boolean(),
});

type PoliceReportFormValues = z.infer<typeof policeReportSchema>;

interface SelectedDepartment {
  id: string;
  name: string;
  stateCode?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export const LiabilityPoliceReportStep: React.FC = () => {
  const { state, dispatch, registerSave, importData } = useIngestion();
  const matterId = state.matterId!;

  // ── Data fetching ──────────────────────────────────────────────────────
  const { data: existingReports, isLoading: isLoadingReports } =
    usePoliceReportsListQuery({ matterId }, { skip: !matterId });
  const { data: auditRecord } = useGetCaseIngestionQuery(matterId);
  const [updateSections] = useUpdateCaseIngestionSectionsMutation();

  const [createPoliceReport] = usePoliceReportsCreateMutation();
  const [deletePoliceReport] = usePoliceReportsDestroyMutation();
  const [updatePoliceReport] = usePoliceReportsPartialUpdateMutation();

  // Track existing report ID for update vs create
  const [existingReportId, setExistingReportId] = useState<string | null>(null);

  // Track selected department for display (picker returns full object)
  const [selectedDepartment, setSelectedDepartment] = useState<SelectedDepartment | null>(null);

  const sectionStatus = auditRecord?.sections?.police_report ?? null;

  // Imported department name for pre-filling search
  const importedDepartmentName =
    importData?.payload?.liability?.police_department?.presence === 'present'
      ? (importData.payload.liability.police_department.value as string)
      : null;

  // ── Hydration ──────────────────────────────────────────────────────────
  const saved = useMemo(() => {
    // Need reports to be loaded before we can hydrate
    if (existingReports === undefined) return null;
    // Also wait for audit record so we know not_found status
    if (auditRecord === undefined) return null;
    const report = existingReports?.[0];
    return {
      report_identifier: report?.report_identifier ?? '',
      police_department_id: report?.police_department?.id ?? '',
      not_found: sectionStatus === 'not_found',
    };
  }, [existingReports, auditRecord, sectionStatus]);

  // Hydrate selected department display from existing report
  const hasHydratedDept = useRef(false);
  useEffect(() => {
    if (hasHydratedDept.current) return;
    const report = existingReports?.[0];
    if (!report?.police_department?.id) return;
    hasHydratedDept.current = true;
    setExistingReportId(report.id);
    setSelectedDepartment({
      id: report.police_department.id,
      name: report.police_department.name || 'Unknown Department',
      stateCode: report.police_department.state_code || undefined,
    });
  }, [existingReports]);

  const imported = useMemo(() => {
    if (!importData?.payload) return null;
    const liab = importData.payload.liability;
    return {
      report_identifier: liab.police_report_number,
    };
  }, [importData]);

  const { form, isAssisted } = useStepHydration<PoliceReportFormValues>({
    schema: policeReportSchema,
    defaults: {
      report_identifier: '',
      police_department_id: '',
      not_found: false,
    },
    saved,
    imported,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const notFound = watch('not_found');
  const departmentId = watch('police_department_id');
  const reportIdentifier = watch('report_identifier');

  const hasReportButNoDept = !!reportIdentifier?.trim() && !departmentId && !notFound;

  // ── Department handlers ────────────────────────────────────────────────
  const handleDepartmentSelected = (department: ReadPoliceDepartment) => {
    setValue('police_department_id', department.id);
    setSelectedDepartment({
      id: department.id,
      name: department.name || 'Unknown Department',
      stateCode: department.state_code || undefined,
    });
  };

  const handleClearDepartment = async () => {
    if (existingReportId) {
      try {
        await deletePoliceReport({ matterId, id: existingReportId }).unwrap();
      } catch {
        toast.error('Failed to remove police report');
        return;
      }
    }
    setExistingReportId(null);
    setValue('police_department_id', '');
    setValue('report_identifier', '');
    setSelectedDepartment(null);
    // Reset audit section
    try {
      await updateSections({
        matterId,
        data: { sections: { police_report: null } },
      }).unwrap();
    } catch {
      // non-blocking
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────
  const saveForm = useCallback(
    async (data: PoliceReportFormValues): Promise<boolean> => {
      const hasDepartment = !!data.police_department_id;
      const hasReportNumber = !!data.report_identifier?.trim();

      // Block save if report number is filled but no department selected
      if (hasReportNumber && !hasDepartment && !data.not_found) {
        toast.error('Please select a police department before saving');
        return false;
      }

      // No action taken — just navigate
      if (!data.not_found && !hasDepartment) {
        return true;
      }

      try {
        // Update audit section
        if (data.not_found) {
          await updateSections({
            matterId,
            data: { sections: { police_report: 'not_found' } },
          }).unwrap();
        } else if (hasDepartment) {
          // Create or update police report
          if (existingReportId) {
            await updatePoliceReport({
              matterId,
              id: existingReportId,
              patchedUpdatePoliceReport: {
                id: existingReportId,
                police_department_id: data.police_department_id ?? null,
                report_identifier: data.report_identifier || null,
              },
            }).unwrap();
          } else {
            const created = await createPoliceReport({
              matterId,
              createPoliceReport: {
                matter_id: matterId,
                police_department_id: data.police_department_id!,
                report_identifier: data.report_identifier || undefined,
                attestation_status: 'attested_unverified',
              },
            }).unwrap();
            setExistingReportId(created.id);
          }

          await updateSections({
            matterId,
            data: { sections: { police_report: 'found' } },
          }).unwrap();
        }

        toast.success('Police report information saved');
        return true;
      } catch {
        toast.error('Failed to save police report information');
        return false;
      }
    },
    [matterId, existingReportId, createPoliceReport, updatePoliceReport, updateSections],
  );

  // Register save for sidebar navigation
  useEffect(() => {
    registerSave(async () => {
      let result = false;
      await handleSubmit(
        async (data) => { result = await saveForm(data); },
        () => { result = false; },
      )();
      return result;
    });
    return () => registerSave(null);
  }, [registerSave, handleSubmit, saveForm]);

  const onSubmit = async (data: PoliceReportFormValues) => {
    const success = await saveForm(data);
    if (success) {
      dispatch({ type: 'GO_TO_STEP', index: state.stepIndex + 1 });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <FormSection
        title="Police Report"
        description="Enter police report details if available"
      >
        <FormGroup>
          {/* Not found checkbox */}
          <FormCheckbox
            label="Police report not available"
            description="Check if no police report was filed or it's unavailable"
            checked={notFound}
            onChange={(e) => setValue('not_found', (e.target as HTMLInputElement).checked)}
            disabled={isSubmitting}
          />

          {!notFound && (
            <>
              {/* Police Department */}
              <FormField>
                <FormLabel>Police Department</FormLabel>
                {selectedDepartment ? (
                  // Selected department card
                  <div
                    data-slot="control"
                    className="flex items-start justify-between rounded-lg border border-zinc-200 bg-white px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-zinc-400" />
                      <div>
                        <p className="text-sm font-medium text-zinc-900">
                          {selectedDepartment.name}
                        </p>
                        {selectedDepartment.stateCode && (
                          <p className="text-xs text-zinc-500">
                            {selectedDepartment.stateCode}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearDepartment}
                      className="rounded p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  // Department picker
                  <div data-slot="control">
                    {importedDepartmentName && (
                      <p className="flex items-center gap-1 text-[11px] text-zinc-400 mb-1.5">
                        <Sparkles className="h-3 w-3 shrink-0 text-zinc-300" />
                        <span>
                          Imported <span className="font-medium text-zinc-500">\&ldquo;{importedDepartmentName}\&rdquo;</span>
                          {' — '}select a matching department below
                        </span>
                      </p>
                    )}
                    <div className="[&_input]:text-sm">
                      <PoliceDepartmentPicker
                        selectedDepartmentId={null}
                        onDepartmentSelect={() => {}}
                        onDepartmentSelected={handleDepartmentSelected}
                        onDepartmentCreated={handleDepartmentSelected}
                        placeholder="Search departments..."
                        initialSearchQuery={importedDepartmentName ?? undefined}
                      />
                    </div>
                  </div>
                )}
              </FormField>

              {/* Report Number — always visible, not blocked by department */}
              <FormField assisted={isAssisted('report_identifier')}>
                <FormLabel htmlFor="v2-report-id">Report Number</FormLabel>
                <FormInput
                  id="v2-report-id"
                  placeholder="e.g., DR-2024-55789"
                  disabled={isSubmitting}
                  {...register('report_identifier')}
                />
                <FormError>{errors.report_identifier?.message}</FormError>
              </FormField>
            </>
          )}
        </FormGroup>
      </FormSection>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting || hasReportButNoDept}
        className={
          'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ' +
          'bg-blue-600 text-white shadow-sm hover:bg-blue-700 ' +
          'disabled:opacity-50 disabled:cursor-not-allowed ' +
          'transition-colors duration-150'
        }
      >
        {isSubmitting ? 'Saving…' : 'Save & Continue'}
      </button>
    </form>
  );
};
